import { RefObject, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { AnalyticsValidationTypesEnum } from 'APIPackage/KnmModule/enums/AnalyticsValidationTypesEnum';
import { KnmDataViolationPathsTemplateEnum } from 'APIPackage/KnmModule/enums/KnmDataViolationPathsTemplateEnum';
import { IKnmDataViolation } from 'APIPackage/KnmModule/interfaces/IKnmDataViolation';
import { HEADER_STICKED_HEIGHT } from 'CorePackage/HeaderModule/constants/headerStickedHeight';
import { Sidebar } from 'CorePackage/MainModule/components/Sidebar';
import { ROUTE_KNM } from 'CorePackage/MainModule/routes/routes';
import { NotMatchingFnsFieldTypesEnum } from 'KnmPackage/MainModule/enums/NotMatchingFnsFieldTypesEnum';
import { getKnmStatusCode, getKnmUrlId, getKnmViolationsData } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmDataViolationExtended } from 'KnmPackage/MainModule/interfaces/IKnmDataViolationExtended';
import { convertKnmViolationToExtended } from 'KnmPackage/MainModule/common/convertKnmViolationToExtended';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { basicMenuItems } from '../constants/basicMenuItems';
import { knmSectionsViolationsPathsTemplatesMap } from '../constants/knmSectionsViolationsPathsTemplatesMap';
import { MenuItemsIdsEnum } from '../enums/MenuItemsIdsEnum';
import { IKnmMenuItem } from '../interfaces/IKnmMenuItem';
import { ViolationsErrorIcon } from './ViolationsErrorIcon';

const replaceFnsDataFieldsMap = {
  [NotMatchingFnsFieldTypesEnum.ORGANIZATION_NAME]: 'Наименование проверяемого лица',
  [NotMatchingFnsFieldTypesEnum.OBJECT_LEGAL_ADDRESS]: 'Объект проведения КНМ',
};

const getMenuSubjectWarning = (notMatchingFnsFields: NotMatchingFnsFieldTypesEnum[]) =>
  notMatchingFnsFields.length > 0 ? (
    <>
      Следующие данные о проверяемом лице не&nbsp;соответствуют данным реестров ФНС:
      <ul>
        {notMatchingFnsFields.map((notMatchingFnsField, index) => (
          <li key={index}>{replaceFnsDataFieldsMap[notMatchingFnsField]}</li>
        ))}
      </ul>
    </>
  ) : null;

interface IAnchor {
  route: string;
  currentAnchorElement: HTMLElement | null;
  nextAnchorElement: HTMLElement | null;
}

interface IKnmErpMenuProps {
  dataRef: RefObject<HTMLDivElement>;
  notMatchingFnsFields: NotMatchingFnsFieldTypesEnum[] | undefined;
  changeResultsOpenedFlags: (id: string, value: boolean) => void;
}

interface IKnmErpMenuStoreProps {
  knmStatusCode: IKnmStatus['code'] | undefined;
  knmDataViolations: IKnmDataViolation[] | undefined;
  knmUrlId: string | undefined;
}

const KnmErpMenuWithoutStore = ({
  dataRef,
  notMatchingFnsFields,
  changeResultsOpenedFlags,
  knmDataViolations,
  knmStatusCode,
  knmUrlId,
}: IKnmErpMenuProps & IKnmErpMenuStoreProps) => {
  const [locationHash, setLocationHash] = useState(window.location.hash);
  const isKnmErpLinksHidden = !knmStatusCode;
  const hiddenItemsIds: MenuItemsIdsEnum[] = useMemo(() => {
    const newHiddenMenuItemsIds: MenuItemsIdsEnum[] = [];

    if (isKnmErpLinksHidden) {
      newHiddenMenuItemsIds.push(MenuItemsIdsEnum.KNM_LINKS);
    }

    return newHiddenMenuItemsIds;
  }, [isKnmErpLinksHidden]);

  const knmDataViolationsExtended: IKnmDataViolationExtended[] | undefined = useMemo(() => {
    return knmDataViolations?.map(knmDataViolation => {
      const knmDataViolationExtended = convertKnmViolationToExtended(knmDataViolation);
      // ToDo: temporary fix - wait backend

      if (
        (knmDataViolationExtended.pathTemplate === KnmDataViolationPathsTemplateEnum.OBJECTS ||
          knmDataViolationExtended.pathTemplate === KnmDataViolationPathsTemplateEnum.OBJECTS_ITEM) &&
        knmDataViolationExtended.type === AnalyticsValidationTypesEnum.RESULT
      ) {
        knmDataViolationExtended.pathTemplate = KnmDataViolationPathsTemplateEnum.RESULT;
      }

      return knmDataViolationExtended;
    });
  }, [knmDataViolations]);

  const menuItems = useMemo(
    () =>
      basicMenuItems.reduce((newMenuItems: IKnmMenuItem[], basicMenuItem) => {
        const newBasicMenuItem = { ...basicMenuItem };

        if (hiddenItemsIds?.some(hiddenItemId => hiddenItemId === newBasicMenuItem.id)) {
          return newMenuItems;
        }
        const violations = knmDataViolationsExtended?.filter(knmDataViolation =>
          knmSectionsViolationsPathsTemplatesMap[newBasicMenuItem.id].includes(knmDataViolation.pathTemplate),
        );

        if (violations?.length) {
          const renderViolationsErrorIcon = () => (
            <ViolationsErrorIcon
              sectionId={newBasicMenuItem.id}
              violations={violations}
              changeResultsOpenedFlags={changeResultsOpenedFlags}
            />
          );

          newBasicMenuItem.renderError = renderViolationsErrorIcon;
        }
        if (newBasicMenuItem.id === MenuItemsIdsEnum.KNM_VERIFIED_PERSON) {
          newBasicMenuItem.warning = notMatchingFnsFields && getMenuSubjectWarning(notMatchingFnsFields);
        }
        newMenuItems.push(newBasicMenuItem);

        return newMenuItems;
      }, []),
    [hiddenItemsIds, knmDataViolationsExtended, notMatchingFnsFields, changeResultsOpenedFlags],
  );

  const [anchors, setAnchors] = useState<IAnchor[]>([]);

  useEffect(() => {
    setAnchors(
      menuItems.map((item, i) => {
        const isLast = menuItems.length === i + 1;
        const anchorId = item.route.replace('#', '');
        const currentAnchorElement = document.getElementById(anchorId);
        const nextAnchorId = !isLast ? menuItems[i + 1].route.replace('#', '') : null;
        const nextAnchorElement = nextAnchorId ? document.getElementById(nextAnchorId) : null;

        return {
          route: item.route,
          currentAnchorElement,
          nextAnchorElement,
        };
      }),
    );
  }, [menuItems]);

  useEffect(() => {
    const updateLocationHash = () => {
      const refOffset = dataRef.current?.getBoundingClientRect().top;

      if (refOffset || refOffset === 0) {
        if (refOffset < HEADER_STICKED_HEIGHT) {
          anchors.forEach(({ route, currentAnchorElement, nextAnchorElement }) => {
            const currentAnchorOffsetTop = currentAnchorElement?.getBoundingClientRect().top!;
            const nextAnchorOffsetTop = nextAnchorElement?.getBoundingClientRect().top!;

            if (
              currentAnchorOffsetTop <= HEADER_STICKED_HEIGHT &&
              nextAnchorOffsetTop > HEADER_STICKED_HEIGHT &&
              window.location.hash !== route
            ) {
              window.history.replaceState(null, '', route);
            }
          });
        } else if (window.location.hash && knmUrlId) {
          window.history.replaceState(null, '', ROUTE_KNM(knmUrlId));
        }
      }
      setLocationHash(window.location.hash);
    };

    updateLocationHash();

    window.addEventListener('scroll', updateLocationHash);

    return () => window.removeEventListener('scroll', updateLocationHash);
  }, [anchors, knmUrlId, dataRef]);

  return <Sidebar type="knm-menu" menuItems={menuItems} locationHash={locationHash} itemsIsAnchors />;
};

const mapStateToProps = (state: IRootState): IKnmErpMenuStoreProps => ({
  knmStatusCode: getKnmStatusCode(state),
  knmDataViolations: getKnmViolationsData(state),
  knmUrlId: getKnmUrlId(state),
});

export const KnmErpMenu = connect(mapStateToProps)(KnmErpMenuWithoutStore);
